import React, { useEffect, useState, useCallback } from "react";
import { Boxes, Building2, Store, Package, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { Modal, Badge, Select } from "../lib/ui";
import { fmtRp, fmtNum, fmtUnit } from "../lib/constants";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

export default function SalesConsumptionReport({ open, onClose }) {
  const { t } = useT();
  const { isRole } = useAuth();
  const isBranch = isRole("BRANCH_STAFF");
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [start, setStart] = useState(monthAgo());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    setData(null);
    const params = new URLSearchParams();
    if (branchId) params.set("branch_id", branchId);
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    api.get(`/sales/consumption/summary?${params.toString()}`).then(({ data }) => setData(data)).catch(() => setData({ rows: [], branch_totals: [], summary: {} }));
  }, [branchId, start, end]);

  useEffect(() => { if (open && !isBranch) api.get("/branches").then(({ data }) => setBranches(data)); }, [open, isBranch]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const s = data?.summary || {};

  return (
    <Modal open={open} onClose={onClose} size="lg" title={t("Sales Consumption Summary")}>
      {/* filters */}
      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="consumption-filters">
        {!isBranch && (
          <Select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="consumption-branch">
            <option value="">{t("All Branches")}</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        )}
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="cf-input" data-testid="consumption-start" />
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="cf-input" data-testid="consumption-end" />
      </div>

      {!data ? (
        <div className="py-10 text-center text-sm text-ink-mute">{t("Loading…")}</div>
      ) : (
        <>
          {/* summary cards */}
          <div className="mb-4 grid grid-cols-3 gap-3">
            <Stat icon={Package} label={t("Products")} value={fmtNum(s.products_consumed || 0)} testid="consumption-kpi-products" />
            <Stat icon={Boxes} label={t("Total Qty")} value={fmtNum(s.total_qty || 0)} testid="consumption-kpi-qty" />
            <Stat icon={Building2} label={t("Est. Cost Value")} value={fmtRp(s.total_value || 0)} testid="consumption-kpi-value" />
          </div>

          {s.menu_items_missing_recipe > 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="consumption-missing-recipe">
              <AlertTriangle size={14} /> {s.menu_items_missing_recipe} {t("menu item(s) sold without a recipe were not consumed")}
            </div>
          )}

          {/* per-branch totals (only in all-branches view) */}
          {!branchId && (data.branch_totals || []).length > 1 && (
            <div className="mb-3 space-y-1.5" data-testid="consumption-branch-totals">
              {data.branch_totals.map((b) => (
                <div key={b.branch_id} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 font-semibold text-ink"><Store size={14} className="text-primary" /> {b.branch_name}</span>
                  <span className="text-ink-soft">{fmtNum(b.total_qty)} {t("units")} · <b className="text-ink">{fmtRp(b.total_value)}</b></span>
                </div>
              ))}
            </div>
          )}

          {/* per-product table */}
          {(data.rows || []).length === 0 ? (
            <div className="py-10 text-center text-sm text-ink-mute" data-testid="consumption-empty">{t("No sales consumption in this period")}</div>
          ) : (
            <div className="max-h-[45vh] overflow-auto rounded-xl border border-line">
              <table className="w-full text-sm" data-testid="consumption-table">
                <thead className="sticky top-0 bg-stone-50"><tr className="text-left text-xs uppercase text-ink-mute">
                  <th className="px-3 py-2">{t("Product")}</th>
                  {!branchId && <th className="px-3 py-2">{t("Branch")}</th>}
                  <th className="px-3 py-2 text-right">{t("Consumed Qty")}</th>
                  <th className="px-3 py-2 text-right">{t("Est. Value")}</th></tr></thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-t border-line/60" data-testid={`consumption-row-${i}`}>
                      <td className="px-3 py-2 font-medium text-ink">{r.product_name}</td>
                      {!branchId && <td className="px-3 py-2 text-ink-soft">{r.branch_name}</td>}
                      <td className="px-3 py-2 text-right font-semibold">{fmtNum(r.quantity)} {fmtUnit(r.unit)}</td>
                      <td className="px-3 py-2 text-right text-ink-soft">{fmtRp(r.est_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl bg-stone-50 p-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-light text-primary"><Icon size={15} /></span>
        <div className="min-w-0"><p className="truncate text-[11px] font-semibold uppercase text-ink-mute">{label}</p><p className="truncate text-base font-extrabold text-ink">{value}</p></div>
      </div>
    </div>
  );
}
